import { useTranslations } from 'next-intl'
import styles from './step3.module.css'
import { IconButton, InputAdornment, TextField } from "@mui/material"
import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { isRequired, isSameAsPassword, mixedLetters, numberOfCharacters, numbersAndSpecialCharacters } from '@/utils/validators'
import { getFieldError, validateForm } from '@/utils/helpers'
import Button, { ButtonSize } from '@/components/UI/Buttons/Button/Button'
import { ForgotPasswordSteps } from '../page'
import { useResetPassword } from '@/apiUtilities/mutations/useResetPassword'
import { useRouter } from 'next/navigation'
import { FieldState, ValidationsForForm } from '@/interfaces/api/forms/forms_types'
import TooltipField from '@/components/UI/Tooltips/TooltipField/TooltipField'
import IconShowPassword from '@/assets/img/Icon_Show-Password.svg'
import IconHidePassword from '@/assets/img/Icon_Hide-Password.svg'
import { toast } from 'react-toastify'
import Notification from '@/components/Notification/Notification'

interface ForgotPasswordStep3FormState {
	password: FieldState<string>
	repeatPassword: FieldState<string>
}

/**
 * Initial (empty) validations object for the login form.
 */
const initialValidations: ValidationsForForm<ForgotPasswordStep3FormState> = {
	password: [],
	repeatPassword: []
}

interface ForgotPasswordStep3Props {
	username: string,
	setStep: (step: ForgotPasswordSteps) => void,
	code: string | null
}

export default function ForgotPassword_Step3 ({ username, setStep, code }: ForgotPasswordStep3Props) {
	const t = useTranslations('ForgotPassword')
	const te = useTranslations('Errors')
	const router = useRouter()
	const resetPassword = useResetPassword()
	const [showPassword, setShowPassword] = useState<boolean>(false)
	const [showRepeatPassword, setShowRepeatPassword] = useState<boolean>(false)
	const [passwordFocused, setPasswordFocused] = useState<boolean>(false)
	const [repeatPasswordFocused, setRepeatPasswordFocused] = useState<boolean>(false)
	const [isFormValid, setIsFormValid] = useState<boolean>(false)
	const [formState, setFormState] = useState<ForgotPasswordStep3FormState>({
		password: {
			value: '',
			dirty: false,
			required: true,
			validators: [
				{ validator: isRequired, show: false },
				{ validator: mixedLetters, show: true },
				{ validator: numbersAndSpecialCharacters, show: true },
				{ validator: (value) => numberOfCharacters(value, 8, 32), show: true }
			]
		},
		repeatPassword: {
			value: '',
			dirty: false,
			required: true,
			validators: [
				{ validator: isRequired, show: false },
				{ validator: isSameAsPassword, show: false }
			]
		}
	})
	const [validations, setValidations] = useState<ValidationsForForm<ForgotPasswordStep3FormState>>(initialValidations)

	/**
	 * Handles input changes for both password and repeatPassword fields.
	 * Marks the field as `dirty` and updates its `value`.
	 * 
	 * @param e - Input change event from the TextField
	 */
	function handleChange(e: ChangeEvent<HTMLInputElement>) {
		const key = e.target.name as keyof ForgotPasswordStep3FormState
		setFormState(prev => {
			return {
				...prev,
				[key]: {
					...prev[key],
					value: e.target.value,
					dirty: true
				}
			}
		})
	}

	/**
   * Runs after a successful reset password.
   * Navigates the user to the login page.
   */
  async function handleSuccessResetPassword () {
    router.push('/login')
  }

	/**
	 * Handles form submission:
	 * - prevents the default browser submit
   * - triggers the reset password mutation
	 * 
	 * @param e - Form submit event
	 */
	function handleSubmit(e:FormEvent) {
		e.preventDefault()
		resetPassword.mutate({ userName: username, code: code!, body: { data: { password: formState.password.value }} }, {
			onSuccess: handleSuccessResetPassword,
			onError: (error) => {
				toast.error(<Notification text={te('defaultUnknowError')} />)
			},
		})
	}

	/**
	 * Re-validates the form whenever a field value changes
	 * Updates both:
   * - `validations` (to show messages in the tooltips)
   * - `formIsValid` (to enable/disable the submit button)
	 */
	useEffect(() => {
		const { validations: newValidations, isValid } = validateForm(formState, initialValidations)
		setValidations(newValidations)
		setIsFormValid(isValid)
	}, [formState])

	const passwordError = getFieldError<ForgotPasswordStep3FormState>(formState, validations, 'password')
  const repeatPasswordError = getFieldError<ForgotPasswordStep3FormState>(formState, validations, 'repeatPassword')

	return (
		<form className={styles.main} onSubmit={handleSubmit} data-testid='Step3'>
			<div className={styles.contentWrapper}>
				<TooltipField
					validations={validations.password}
					placement="bottom-start"
					arrow={false}
					disableHoverListener={true}
					disableTouchListener={true}
					hasHelperText={true}
					open={passwordFocused}
					>
					<TextField
						data-testid='Step3_password'
						type={showPassword ? 'text' : 'password'}
						name="password"
						label={t('password')}
						value={formState.password.value}
						onChange={handleChange}
						error={passwordError.hasError && !passwordFocused}
						helperText={!passwordFocused && passwordError.hasError ? t(`messages.fields.step3PasswordError`) : ' '}
						autoFocus={true}
						onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
						slotProps={{
							input: {
								endAdornment:
								<InputAdornment position="end">
									<IconButton
										onClick={() => setShowPassword(prev => !prev)}
										edge="end"
									>
										{showPassword
											? <IconHidePassword className={styles.passwordIcon} />
											: <IconShowPassword className={styles.passwordIcon} />
										}
									</IconButton>
								</InputAdornment>
							}
						}}
					/>
				</TooltipField>

				<TooltipField
					validations={validations.repeatPassword}
					placement="bottom-start"
					arrow={false}
					disableHoverListener={true}
					disableTouchListener={true}
					hasHelperText={true}
					open={repeatPasswordFocused}
					>
					<TextField
						data-testid='Step3_repeatPassword'
						type={showRepeatPassword ? 'text' : 'password'}
						name="repeatPassword"
						label={t('repeatPassword')}
						value={formState.repeatPassword.value}
						onChange={handleChange}
						error={repeatPasswordError.hasError && !repeatPasswordFocused}
						helperText={!repeatPasswordFocused && repeatPasswordError.hasError ? t(`messages.fields.step3RepeatPasswordError`) : ' '}
						onFocus={() => setRepeatPasswordFocused(true)}
            onBlur={() => setRepeatPasswordFocused(false)}
						slotProps={{
							input: {
								endAdornment:
								<InputAdornment position="end">
									<IconButton
										onClick={() => setShowRepeatPassword(prev => !prev)}
										edge="end"
									>
										{showRepeatPassword
											? <IconHidePassword className={styles.passwordIcon} />
											: <IconShowPassword className={styles.passwordIcon} />
										}
									</IconButton>
								</InputAdornment>
							}
						}}
					/>
				</TooltipField>
			</div>

			<Button data-testid='Step3_button' type={"submit"} size={ButtonSize.LARGE} disabled={!isFormValid} className={styles.nextBtn}>
				{t('nextButton')}
			</Button>
		</form>
	)
}